import { Link, type LinkProps } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import type { Product, Variant } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';

import {
  VariantEditorRail,
  type VariantRailHeading,
  type VariantRailMeta,
} from './variant-editor-rail';

export interface VariantPageFrameProps {
  product: Product;
  variant: Variant;
  /** Every variant of the product, in position order. */
  variants: readonly Variant[];
  backTo: LinkProps;
  backLabel: string;
  /** Where another variant of the same product opens. */
  linkTo: (variantId: string) => LinkProps;
  railMeta: (variant: Variant) => VariantRailMeta;
  railHeading?: VariantRailHeading;
  children: ReactNode;
}

/**
 * What the master and the store variant page share: the header with previous and next, the rail
 * on the left, the page's own cards on the right (ADR 97).
 */
export function VariantPageFrame({
  product,
  variant,
  variants,
  backTo,
  backLabel,
  linkTo,
  railMeta,
  railHeading,
  children,
}: VariantPageFrameProps) {
  const { t } = useTranslation('products');
  const index = variants.findIndex((v) => v.id === variant.id);
  const previous = index > 0 ? variants[index - 1] : undefined;
  const next = index >= 0 && index < variants.length - 1 ? variants[index + 1] : undefined;
  const step = (target: Variant | undefined, label: string, icon: ReactNode) =>
    target ? (
      <Button asChild variant="outline" size="icon-sm">
        <Link {...linkTo(target.id)} aria-label={label}>
          {icon}
        </Link>
      </Button>
    ) : (
      <Button variant="outline" size="icon-sm" disabled aria-label={label}>
        {icon}
      </Button>
    );

  return (
    <PageContent>
      <PageHeader
        title={variant.title}
        description={t('variantPage2.position', {
          position: index + 1,
          total: variants.length,
          product: backLabel,
        })}
        backTo={backTo}
        backLabel={backLabel}
        actions={
          <div className="flex items-center gap-1">
            {step(previous, t('variantPage.previous'), <ChevronLeft aria-hidden />)}
            {step(next, t('variantPage.next'), <ChevronRight aria-hidden />)}
          </div>
        }
      />
      <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,19rem)_minmax(0,1fr)]">
        <div className="lg:sticky lg:top-4">
          <VariantEditorRail
            product={product}
            variants={variants}
            currentId={variant.id}
            linkTo={linkTo}
            productLink={backTo}
            meta={railMeta}
            heading={railHeading}
          />
        </div>
        {children}
      </div>
    </PageContent>
  );
}
